import React, { useState, useEffect } from 'react';
import emptyPlate from './images/emptyPlate.png';

import { useNavigate } from "react-router-dom";

import AppRoutes from '../../constants/AppRoutes';
import Messages from '../../constants/Messages';
import ErrorUtils from '../../utilities/error-utils';

import Loader from '../../components/loader/loader';
import Header from '../../components/header/header';
import Item from '../../components/item/item';
import Button from '../../components/form-elements/button';
import InlineMessageBlock from '../../components/inline-message-block/inline-message-block';

import CartService from '../../services/cart-service';
import AuthenticationService from '../../services/authentication-service';
import { useAuth } from '../../hooks/useAuth';
import ProfileCard from '../../components/profile-card/profile-card';
import Checkbox from '../../components/checkbox/checkbox';

import iIcon from './images/iIcon.svg';

const Cart = () => {
    const navigate = useNavigate();
    const { authLogout } = useAuth();

    const [totalBalance, setTotalBalance] = useState(0);
    const [items, setItems] = useState([]);
    const [cartQty, setCartQty] = useState(0);
    const [cartTotalValue, setCartTotalValue] = useState(0);
    const [error, setError] = useState();
    const [errorCode, setErrorCode] = useState();
    const [loader, setLoader] = useState(false);
    const [isEventTime, setIsEventTime] = useState(false);
    const [isEventCash, setIsEventCash] = useState(true);
    const [currentBalance, setCurrentBalance] = useState({})
    const [showCDWCashBalance, setShowCDWCashBalance] = useState(true);
    const [isClearCart, setIsClearCart] = useState(false);

    const handleChange = (cashType) => {
        if (cashType === Messages.CART.EVENT_CASH_OPTION.VALUE) {
            setIsEventCash(!isEventCash);
        }
    }

    useEffect(() => {
        (async () => {
            const cart = CartService.getCart();
            setItems(cart.items);
            updateTotalQtyAndTotalValue(cart.totalCartValue, cart.totalCartQty);
            const balance = await AuthenticationService.getUserBalance();
            setCurrentBalance(balance);
            setTotalBalance(balance && balance?.event ? (balance?.cdw?.amount + balance?.event?.amount) : balance?.cdw?.amount);
            setIsEventTime(balance && balance?.event ? true : false);
            
            const userDetails = await AuthenticationService.getUserDetails();
            let userEventDetails = AuthenticationService.getEventDetails();
            let userBaseLocation = userDetails.locationDetails.name;
            let userSelectedLocation = userEventDetails ? userEventDetails.user.selectedLocation : undefined;
            
            setShowCDWCashBalance(userBaseLocation === userSelectedLocation);
            if(balance && balance?.event && userBaseLocation !== userSelectedLocation) setTotalBalance(balance?.event?.amount);
        })();
    }, []);


    /** ------------------------------------------------------------------
     * Method : updateTotalQtyAndTotalValue
     * @param {*} totalValue 
     * @param {*} totalValue 
     */
    const updateTotalQtyAndTotalValue = (totalValue, totalQuantity) => {
        setCartTotalValue(totalValue);
        setCartQty(totalQuantity);
    }

    /** ------------------------------------------------------------------
     * Method : updateCart
     * Description : Update the items in the cart and return its count and total value
     * @param {*} item 
     * @param {*} qty 
     */
    const updateCart = async (item, qty) => {
        const { totalCartQty, totalCartValue } = await CartService.updateCart(item, qty);
        updateTotalQtyAndTotalValue(totalCartValue, totalCartQty);
    }

    const removeItemFromCart = async (itemId) => {
        await CartService.removeItemFromCart(itemId);
        window.location.reload();       // Reload to update the latest from LS
    }

    /** ------------------------------------------------------------------
     * Method : makePayment
     * Description : Pay to Vendor
     */
    const makePayment = async () => {
        setLoader(true);
        try {
            let userEventDetails = await AuthenticationService.getEventDetails();
            let userSelectedLocation = userEventDetails ? userEventDetails.user.selectedLocation : undefined;
            let locationDetails = await AuthenticationService.getLocationDetails();
            let currentLocation;
            if(locationDetails) {
                for(let location of locationDetails) {
                    if(location.location_name === userSelectedLocation) {
                        currentLocation = location;
                        break;
                    }
                }
            } else {
                let userDetails = await AuthenticationService.getUserDetails();
                currentLocation = userDetails?.locationDetails;
            }
            const isPaymentSuccess = await CartService.buyItems(items, isEventCash, currentBalance, cartTotalValue, !showCDWCashBalance, currentLocation);
            if (isPaymentSuccess) {
                navigate(AppRoutes.ORDER_CONFIRM, {
                    state: {
                        amountPaid: cartTotalValue,
                        items: JSON.stringify(items)
                    }
                });
            }
        } catch (e) {
            // Unprocessed Error in coming
            // Processing It
            const errorData = e?.response?.data || undefined;
            if(errorData?.error?.code === Messages.ERROR_CODES.EVENT_EXPIRED.VALUE || errorData?.error?.code === Messages.ERROR_CODES.EVENT_STARTED.VALUE) {
                navigate(AppRoutes.HOME);
            }
            const error = ErrorUtils.processError(e);

            if (error.code === Messages.ERROR_CODES.INVALID_JWT.code) {
                await authLogout();
                navigate(AppRoutes.LOGIN);
            } else {
                if (errorData) {
                    if (errorData?.data?.missingItemIds) {
                        const { totalCartQty, totalCartValue } = await CartService.updateMissingItems(errorData.data.missingItemIds);
                        updateTotalQtyAndTotalValue(totalCartValue, totalCartQty);
                    }
                    else if(errorData?.error?.code === Messages.ERROR_CODES.ITEMS_MISMATCH.VALUE) {
                        setIsClearCart(true);
                        error.title = Messages.ERROR_CODES.ITEMS_MISMATCH.title;
                        error.message = Messages.ERROR_CODES.ITEMS_MISMATCH.message;
                        error.actionLabel = Messages.CART.GO_TO_HOME;
                        error.actionCallback = () => {
                            CartService.clearCart();
                            navigate(AppRoutes.HOME)
                        };
                    } else if(errorData?.error?.code === Messages.ERROR_CODES.BASE_LOCATION_MISMACTH.VALUE) {
                        error.title = Messages.ERROR_CODES.BASE_LOCATION_MISMACTH.title;
                        error.message = Messages.ERROR_CODES.BASE_LOCATION_MISMACTH.message;
                        error.actionLabel = Messages.CART.GO_TO_LOGIN;
                        error.actionCallback = async () => {
                            await authLogout();
                            navigate(AppRoutes.LOGIN);
                        };
                    }
                    setError(error);
                    setErrorCode(errorData?.error?.code);
                } else {
                    setError(error)
                }
            }
        }
        setLoader(false);
    };

    /** ------------------------------------------
     * Method : backToShop
     * Description : Navigate back to shop page
     */
    const backToShop = () => {
        navigate(AppRoutes.HOME);
    };

    function renderMessageBlock() {
        if (!isEventCash && currentBalance.cdw.amount < cartTotalValue) {
            return <InlineMessageBlock
                title={Messages.CART.LOW_CDW_BALANCE.TITLE}
                message={Messages.CART.LOW_CDW_BALANCE.MESSAGE} />
        } else if (totalBalance >= cartTotalValue) {
            return <Button onClick={makePayment}>{Messages.CART.PAY_NOW}</Button>
        } 
        else {
            return <InlineMessageBlock
                title={Messages.CART.LOW_BALANCE.TITLE}
                message={Messages.CART.LOW_BALANCE.MESSAGE} />
        }
    }

    return (
        <div className='bg-gray-100'>
            <Header isPrevActive={true} prevNavigationText={Messages.SHOP.TITLE} prevPath={AppRoutes.HOME} isClearCart={isClearCart}/>
            <div className='mx-auto max-w-lg min-h-screen pt-4'>
                {isEventTime ? (<ProfileCard isEventTime={isEventTime} showCDWCashBalance={showCDWCashBalance} />) : null}
                <div className='container p-4 mx-auto'>
                    <h2 className='font-bold'>{Messages.CART.TITLE} ({cartQty})</h2>
                    <ul>
                        {
                            items && items.length > 0 ? items.map((item) => {
                                return <Item
                                    data={item}
                                    key={item.id}
                                    updateQty={updateCart}
                                    removeItem={removeItemFromCart}
                                />
                            }) : (
                                <div>
                                    <div className='w-2/3 text-center mx-auto p-10 pb-0 mb-4'>
                                        <img src={emptyPlate} alt="Empty Cart" />
                                        <h1>{Messages.CART.NO_ITEMS}</h1>
                                    </div>
                                    <Button onClick={backToShop}>{Messages.CART.BACK_TO_SHOP}</Button>
                                </div>
                            )
                        }
                    </ul>
                </div>
                {
                    items && items.length > 0 ? (
                        <div className='container p-4 mx-auto'>
                            <h2 className='font-bold'>{Messages.CART.PAYMENT_HEADING}</h2>
                            <div className='bg-white p-4 mt-2 rounded-lg'>
                                <div className='flex justify-between mb-2 font-bold'>
                                    <div className=''>{Messages.CART.TOTAL_BILL}</div>
                                    <div className=''>{Messages.CURRENCY.INR} {cartTotalValue}</div>
                                </div>
                                <div className='text-xs mt-4 mb-4 text-gray-400'>
                                    {Messages.CART.DELIVERY_MESSAGE}
                                </div>
                                {
                                    isEventTime && currentBalance.event.amount > 0 ? (
                                        <>
                                            {
                                                isEventCash && currentBalance.event.amount < cartTotalValue && cartTotalValue <= totalBalance ? (
                                                    <div className='flex gap-2.5 mt-4 mb-4'>
                                                        <img src={iIcon} className="h-[15px] w-[15px]" alt="I Icon" />
                                                        <p className='text-xs font-medium'>{Messages.CART.COMBINED_TRANSACTION_NOTE}</p>
                                                    </div>
                                                ) : ( !showCDWCashBalance ? (
                                                    <div className='flex gap-2.5 mt-4 mb-4'>
                                                        <img src={iIcon} className="h-[15px] w-[15px]" alt="I Icon" />
                                                        <p className='text-xs font-medium'>{Messages.CART.DIFF_LOCATION_TRANSACTION_NOTE}</p>
                                                    </div>
                                                ): null)
                                            }
                                            {showCDWCashBalance ? <div className='flex gap-2.5 mt-4 mb-4'>
                                                <Checkbox value={Messages.CART.EVENT_CASH_OPTION.VALUE} checked={isEventCash} onCheck={() => handleChange(Messages.CART.EVENT_CASH_OPTION.VALUE)} />
                                                <p className='text-xs'>{Messages.CART.EVENT_CASH_OPTION.MESSAGE}</p>
                                            </div> : null}
                                        </>
                                    ) : null
                                }
                                {
                                    renderMessageBlock()
                                }
                                {
                                    error ? (        
                                        <InlineMessageBlock
                                            title={error.title}
                                            message={error.message}
                                            {...(errorCode === Messages.CART.INSUFFICIENT_BALANCE && {
                                                actionLabel: error.actionLabel
                                                ? error.actionLabel
                                                : Messages.CART.TRY_AGAIN,
                                                actionCallback: error.actionCallback
                                                ? error.actionCallback
                                                : makePayment,
                                            })}
                                            />
                                    ) : null
                                }
                            </div>
                        </div>
                    ) : null
                }
                <Loader show={loader} fullPage={true} message={Messages.CART.PAYMENT_IN_PROGRESS} />
            </div>
        </div>
    )
}

export default Cart;





